import Link from "@/src/common/components/Link"
import { MOBILE_PREFERRED_MORE_MENU_COPY } from "@/src/sb/business-layer/config"
import User from "@/src/sb/components/icon/User"
import { ReactNode } from "react"

import Avatar from "../Avatar"
import Menu from "../Menu"
import Typography from "../Typography"
import { useDropdown } from "./hooks"

export const NAV_TYPES = {
  home: {
    src: "/assets/icons/home.svg",
    label: "Home"
  },
  moreMenu: {
    src: "/assets/icons/topics-outlined.svg",
    label: MOBILE_PREFERRED_MORE_MENU_COPY
  },
  account: {
    src: "/assets/icons/person-outlined.svg",
    label: "Account"
  },
  help: {
    src: "/assets/icons/help-outlined.svg",
    label: "Help"
  }
}

type NavProps = {
  type: keyof typeof NAV_TYPES
  dot?: "green" | "red"
}

export type MobileDropdownProps = NavProps & {
  children: ReactNode
  menuAlign?: "right" | "left" | "center"
  columns?: "one" | "three"
}

const NAV_ITEM_CONTROL =
  "cursor-pointer bg-stears_black dark:bg-transparent py-3.5 min-w-[calc(100vw/4)] dark:min-w-fit inline-block text-stears_white dark:p-0 dark:!leading-[18px]"
const Dropdown = (props: MobileDropdownProps) => {
  const { children, type, menuAlign = "left", columns } = props

  const { showMenu, handleShowMenu, parentElementRef, keyboardHandler } =
    useDropdown()

  const { label } = NAV_TYPES[type]
  return (
    <div className="text-stears_white inline-block" ref={parentElementRef}>
      <div onKeyDown={keyboardHandler}>
        <Menu
          columns={columns}
          position={`top-${menuAlign}`}
          visible={showMenu}
        >
          {children}
        </Menu>
      </div>
      <div>
        <button
          type="button"
          className={NAV_ITEM_CONTROL}
          onClick={handleShowMenu(!showMenu)}
          aria-expanded={showMenu}
          aria-haspopup={true}
          aria-hidden={!showMenu}
          aria-label={label}
        >
          <div className="flex justify-center relative">
            <div className={"inline-block dark:hidden shrink"}>
              <User size={32} />
            </div>
            <span className={"hidden dark:inline-block"}>
              <User size={28} />
            </span>
          </div>

          <div className="mt-2 inline-block dark:mt-0 text-gray-400 dark:!leading-[18px]">
            <p
              className={
                "text-[12px] leading-3 dark:!leading-[18px] dark:text-xs"
              }
            >
              {label}
            </p>
          </div>
        </button>
      </div>
    </div>
  )
}

export type MobileLinkProps = NavProps & {
  href: string
  onClick?: (label: string) => void
}

const MobileNavLink = (props: MobileLinkProps) => {
  const { href, dot, type, onClick } = props
  const { label, src } = NAV_TYPES[type]

  return (
    <div>
      <div>
        <Link legacyBehavior href={href}>
          <a className={NAV_ITEM_CONTROL} onClick={() => onClick?.(label)}>
            <div className="flex justify-center">
              <Avatar
                size="sm"
                src={src}
                alt={label}
                dot={dot}
                dotRing={false}
              />
            </div>

            <div className="mt-2 text-center text-gray-400">
              <Typography type="btn_12">{label}</Typography>
            </div>
          </a>
        </Link>
      </div>
    </div>
  )
}

export type MobileNavProps = (MobileLinkProps | MobileDropdownProps) & NavProps

Dropdown.displayName = "MobileNavItem.Dropdown"
MobileNavLink.displayName = "MobileNavItem.Link"

export const MobileNavItem = {
  Dropdown,
  Link: MobileNavLink
}

export default MobileNavItem
